/**
 * InteropsThreadPanel (#328 Phase 2) — the incident work-surface container for the two-way,
 * MULTI-DESTINATION inter-ops information-sharing thread. It loads the context's thread, renders
 * each message with the #452 / A.5 byline ("From <desk> — to <audience>"), a kind chip, the body,
 * and its per-recipient RecipientBreakdown; and it offers a multi-destination composer whose
 * recipient set is NEVER a fixed dropdown — it is the in-app agencies picker (COP counterparties)
 * PLUS the out-of-app delivery_destinations registry (ISC / Public Safety Canada / ministries).
 *
 * The interops message is one-author → N-recipients, so it uses its OWN renderer (MessageThread's
 * binary requestor/operator model does not fit a multi-party thread — that component was
 * parameterised only to fix the agency-task "Pilot" leak). An acknowledgement records a RECEIPT
 * only (A.6); an out-of-app leg is "Sent", never proven read (R3).
 */
import { useEffect, useState, type JSX } from 'react';
import type {
  AgencyView,
  DeliveryDestination,
  InteropsMessageKind,
  InteropsMessageView,
} from '@tsr/shared-types';
import type { InteropsRecipientView } from '@tsr/shared-types';
import {
  fetchAgencies,
  fetchDeliveryDestinations,
  fetchInteropsThread,
  sendInteropsMessage,
  acknowledgeInteropsMessage,
  recordInteropsResponse,
  type SendInteropsMessageInput,
} from './api';
import { getSessionId } from './lib/operatorActions';
import { getSeatName } from './lib/seatIdentity';
import { RecipientBreakdown } from './RecipientBreakdown';
import {
  INFORMATION_SHARING_LABEL,
  INFORMATION_SHARING_SUBTEXT,
  INFORMATION_SHARING_EMPTY,
  ONWARD_HANDLING_LABEL,
  responseRecordedAtLabel,
  INFORMATION_KIND_LABEL,
  RFA_KIND_LABEL,
  REQUEST_ASSISTANCE_LABEL,
  interopsKindLabel,
  authorBylineLabel,
  SEND_LABEL,
  SENDING_LABEL,
  COMPOSER_PLACEHOLDER,
  COMPOSER_RECIPIENTS_LABEL,
  FN_PRESET_LABEL,
  outboundToLabel,
  ACKNOWLEDGE_RECEIPT_LABEL,
  RFA_FEDERAL_CAPTION,
  RECORD_RESPONSE_LABEL,
  RECORD_RESPONSE_DIALOG_TITLE,
  RECORD_RESPONSE_DIALOG_HINT,
  RECORD_RESPONSE_PLACEHOLDER,
  RECORD_RESPONSE_CANCEL_LABEL,
  RECORDED_FROM_OUTSIDE_COP_SUBLABEL,
  RECORDED_SOURCE_FALLBACK,
  RECORDING_AGENCY_FALLBACK,
  recordedResponseChromeLabel,
} from './interopsVocabulary';

/** Compact `HH:MM UTC` from an ISO stamp (deterministic — mirrors RecipientBreakdown). */
function hhmmUtc(iso: string): string {
  return iso.length >= 16 ? `${iso.slice(11, 16)} UTC` : '';
}

// The in-app COP counterparties an operator can share with (municipalities, First Nations, and
// the provincial agencies). Drone-operator agencies are NOT inter-ops counterparties.
const IN_APP_AGENCY_KINDS = ['municipal', 'first_nation', 'provincial'] as const;

/**
 * #451 — derive the originator's reply target from the loaded thread: the PEOC counterparty the
 * reply is addressed to (an IN-APP agency, so the PEOC acknowledges it in-COP — never an
 * out-of-app leg; founder §1 workflow-completeness), plus the message being answered (in_reply_to).
 *
 * REQUIRES `viewerAgencyId` (the acting seat) — the counterparty is "the in-app party that is NOT
 * the viewer", and without the viewer's identity that cannot be told apart from a self-send (which
 * the route rejects as recipient_is_author). So it returns null when the seat is unknown → the
 * reply composer does not render (dormant under the AUTH-off single-seat demo, where the originator
 * and PEOC collapse to one agency anyway — #551 — and activating with the enforced identity, #190).
 *
 * Newest→oldest: prefer the author of the latest COUNTERPARTY message (the PEOC's onward leg /
 * information reply / recorded response — the thing being answered); fall back to the in-app
 * RECIPIENT of the viewer's own message (the PEOC the request was addressed to) when the PEOC has
 * authored nothing yet. Returns null when nothing is agency-addressable.
 */
export function deriveReplyTarget(
  messages: InteropsMessageView[],
  viewerAgencyId?: string,
): { recipientAgencyId: string; recipientName: string; inReplyTo: string } | null {
  if (!viewerAgencyId) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.author_agency_id && m.author_agency_id !== viewerAgencyId) {
      return {
        recipientAgencyId: m.author_agency_id,
        recipientName: m.author_agency_name ?? '',
        inReplyTo: m.id,
      };
    }
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    const rc = m.recipients.find(
      (r) => r.recipient_agency_id != null && r.recipient_agency_id !== viewerAgencyId,
    );
    if (rc?.recipient_agency_id) {
      return {
        recipientAgencyId: rc.recipient_agency_id,
        recipientName: rc.recipient_agency_name ?? '',
        inReplyTo: m.id,
      };
    }
  }
  return null;
}

function audienceSummary(m: InteropsMessageView): string {
  if (m.recipients.length === 0) return 'recipients';
  if (m.recipients.length === 1) {
    const r = m.recipients[0]!;
    return r.recipient_agency_name ?? r.recipient_destination_name ?? 'recipient';
  }
  return `${m.recipients.length} recipients`;
}

function hasFederalRecipient(m: InteropsMessageView): boolean {
  return m.recipients.some((r) => r.recipient_destination_kind === 'federal');
}

export function InteropsThreadPanel({
  contextType,
  contextId,
  viewerAgencyId,
  defaultKind,
  hideHeading = false,
  readOnly = false,
  composerMode,
}: {
  contextType: InteropsMessageView['context_type'];
  contextId: string | null;
  /** The acting seat's agency (AUTH-era; when known, enables precise not-self exclusion + the
   *  per-recipient acknowledge, and — #451 — the reply-composer's counterparty derivation).
   *  Omitted under AUTH-off single-seat demo. */
  viewerAgencyId?: string;
  /** Seeds the composer's kind (the operator can still change it). The assistance_request mount
   *  (#328c / R4) passes 'request_for_assistance' so requesting assistance on an assist file is
   *  the default; incident/standalone mounts omit it → 'information'. */
  defaultKind?: InteropsMessageKind;
  /** Suppress the panel's OWN "Information sharing" heading when the mount already sits under a
   *  section header — otherwise the label doubles (founder polish 2026-07-06: the #552 standalone
   *  "Not attached to an incident" section renders its own header, so the panel's is redundant).
   *  Defaults false: the incident + assistance-request mounts keep their heading. */
  hideHeading?: boolean;
  /** RBAC Slice 3c — the ORIGINATOR's READ-ONLY view of the PEOC's onward handling of the
   *  request it raised. Suppresses the composer, the acknowledge control, and the record-response
   *  affordance; the header becomes "Onward handling"; a recorded reply renders party-neutrally as
   *  "Response recorded · HH:MM". When there is no onward handling yet the panel renders nothing
   *  (no empty-thread clutter in the requestor tray). Defaults false. LEGACY ALIAS for
   *  `composerMode='none'` (kept so the shipped read-only mount + its lock test stay byte-identical);
   *  `composerMode`, when passed, TAKES PRECEDENCE over this flag. */
  readOnly?: boolean;
  /** #451 — the composer MODE. `'full'` = the multi-destination composer (incident / standalone /
   *  the PEOC's assistance-request surface). `'none'` ≡ readOnly (the originator's read-only
   *  onward-handling view). `'reply'` = the originator's read-only view PLUS a constrained reply
   *  composer whose destination is LOCKED to the PEOC counterparty (derived from the thread) and
   *  whose kind is Information — closing the "answer the PEOC by phone" fallback (#451). Undefined
   *  → derived from `readOnly` (back-compat). */
  composerMode?: 'none' | 'reply' | 'full';
}): JSX.Element {
  // `composerMode` is authoritative when passed; else the legacy `readOnly` boolean maps to
  // 'none' (read-only) / 'full' (the composer). All shipped mounts pass neither `composerMode`
  // nor become anything but their prior behaviour → byte-identical.
  const mode: 'none' | 'reply' | 'full' = composerMode ?? (readOnly ? 'none' : 'full');
  // The originator's onward-handling framing (read-only header + party-neutral cards + no
  // acknowledge / record affordance) covers BOTH the pure read-only view AND the reply view;
  // the reply view only ADDS a constrained composer below the read-only thread.
  const isOriginatorView = mode !== 'full';
  const [messages, setMessages] = useState<InteropsMessageView[]>([]);
  const [agencies, setAgencies] = useState<AgencyView[]>([]);
  const [destinations, setDestinations] = useState<DeliveryDestination[]>([]);
  const [kind, setKind] = useState<InteropsMessageKind>(defaultKind ?? 'information');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set()); // "a:<id>" | "d:<id>"
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #567 — the "Record response" paste dialog: which out-of-app leg of which message.
  const [recording, setRecording] = useState<{
    messageId: string;
    recipient: InteropsRecipientView;
  } | null>(null);
  const [recordSaving, setRecordSaving] = useState(false);
  // #451 — the originator's constrained reply composer (a single locked-destination body + Send).
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  async function reloadThread(signal?: AbortSignal): Promise<void> {
    const t = await fetchInteropsThread(contextType, contextId, signal);
    setMessages(t.messages);
  }

  useEffect(() => {
    const ac = new AbortController();
    void reloadThread(ac.signal).catch(() => {});
    // Only the FULL composer needs the multi-destination recipient picker. The read-only and
    // reply views have no picker (reply's destination is LOCKED to the PEOC, derived from the
    // thread) — so their picker data is not fetched.
    if (mode === 'full') {
      void fetchAgencies(ac.signal, { kinds: IN_APP_AGENCY_KINDS })
        .then((a) => setAgencies(a.filter((x) => x.id !== viewerAgencyId)))
        .catch(() => {});
      void fetchDeliveryDestinations(ac.signal)
        .then((d) => setDestinations(d.filter((x) => x.active)))
        .catch(() => {});
    }
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextType, contextId, viewerAgencyId, mode]);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** FN one-act convenience (PERP §6.7.1): co-select Indigenous Services Canada (+ the PEOC
   *  destination when present) so the request notifies both in one send. */
  function applyFnPreset(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      const isc = destinations.find((d) => /indigenous services canada/i.test(d.name));
      if (isc) next.add(`d:${isc.id}`);
      const peoc = destinations.find((d) => d.kind === 'peoc');
      if (peoc) next.add(`d:${peoc.id}`);
      return next;
    });
  }

  function buildRecipients(): SendInteropsMessageInput['recipients'] {
    return [...selected].map((key) => {
      const [t, id] = key.split(':');
      return t === 'a' ? { agency_id: id } : { destination_id: id };
    });
  }

  async function handleSend(): Promise<void> {
    if (body.trim().length === 0 || selected.size === 0) return;
    setSending(true);
    setError(null);
    try {
      await sendInteropsMessage(
        {
          context_type: contextType,
          context_id: contextId,
          kind,
          body: body.trim(),
          recipients: buildRecipients(),
        },
        getSessionId(),
      );
      setBody('');
      setSelected(new Set());
      await reloadThread();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'send failed');
    } finally {
      setSending(false);
    }
  }

  async function handleAck(messageId: string, recipientId: string): Promise<void> {
    try {
      await acknowledgeInteropsMessage(messageId, recipientId, getSessionId(), getSeatName() ?? undefined);
      await reloadThread();
    } catch {
      /* best-effort — the state refreshes on next load */
    }
  }

  /** #567 — persist a pasted-back out-of-app reply as a recorded document, then refresh. */
  async function handleRecordResponse(text: string): Promise<void> {
    if (!recording || text.trim().length === 0) return;
    setRecordSaving(true);
    setError(null);
    try {
      await recordInteropsResponse(recording.messageId, recording.recipient.id, text.trim(), getSessionId());
      setRecording(null);
      await reloadThread();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'record failed');
    } finally {
      setRecordSaving(false);
    }
  }

  const canSend = body.trim().length > 0 && selected.size > 0 && !sending;

  // #451 — the reply target (the PEOC counterparty + the message being answered), derived from
  // the loaded thread. Only computed in reply mode; null (no composer) when it cannot be derived.
  const replyTarget = mode === 'reply' ? deriveReplyTarget(messages, viewerAgencyId) : null;

  /** #451 — send the originator's reply: kind Information, destination LOCKED to the derived PEOC
   *  counterparty (in-app, so the PEOC acknowledges it in-COP — the action starts AND finishes in
   *  the COP, founder §1), threaded via in_reply_to to the message being answered. */
  async function handleReply(): Promise<void> {
    if (!replyTarget || replyBody.trim().length === 0) return;
    setReplySending(true);
    setReplyError(null);
    try {
      await sendInteropsMessage(
        {
          context_type: contextType,
          context_id: contextId,
          kind: 'information',
          body: replyBody.trim(),
          in_reply_to: replyTarget.inReplyTo,
          recipients: [{ agency_id: replyTarget.recipientAgencyId }],
        },
        getSessionId(),
      );
      setReplyBody('');
      await reloadThread();
    } catch (e) {
      setReplyError(e instanceof Error ? e.message : 'send failed');
    } finally {
      setReplySending(false);
    }
  }
  const canReply = replyBody.trim().length > 0 && replyTarget != null && !replySending;

  // Originator view (read-only OR reply): nothing to show until the PEOC has recorded onward
  // handling — render nothing rather than an empty "No information shared yet" row per request in
  // the tray (and, in reply mode, there is nothing to reply to yet).
  if (isOriginatorView && messages.length === 0) return <></>;

  return (
    <section className="border-t border-slate-800 pt-3" data-testid="interops-thread-panel">
      {!hideHeading && (
        <div className="mb-1.5" data-testid="interops-thread-heading">
          <h3 className="text-xs uppercase tracking-wider text-slate-400">
            {isOriginatorView ? ONWARD_HANDLING_LABEL : INFORMATION_SHARING_LABEL}
          </h3>
          {!isOriginatorView && (
            <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{INFORMATION_SHARING_SUBTEXT}</p>
          )}
        </div>
      )}

      {/* Message list */}
      <ul className="mb-3 flex max-h-80 flex-col gap-2 overflow-y-auto" data-testid="interops-message-list">
        {messages.length === 0 ? (
          <li className="text-[11px] text-slate-500">{INFORMATION_SHARING_EMPTY}</li>
        ) : (
          messages.map((m) => {
            // #567 — a recorded reply renders as a distinct recorded-document card (not the
            // one-author→N-recipients composer card; it has no recipients / no ack).
            if (m.kind === 'recorded_response') {
              return <RecordedResponseCard key={m.id} message={m} readOnly={isOriginatorView} />;
            }
            const myUnacked = viewerAgencyId
              ? m.recipients.find((r) => r.recipient_agency_id === viewerAgencyId && !r.acknowledged_at)
              : undefined;
            return (
              <li
                key={m.id}
                data-testid="interops-message"
                className="rounded-lg border border-slate-800 bg-slate-900/50 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-400">
                    {authorBylineLabel(m.author_agency_name ?? 'EOC', audienceSummary(m))}
                  </span>
                  <span
                    data-testid="interops-kind-chip"
                    className={`rounded px-1.5 py-px text-[9px] font-medium uppercase tracking-wide ${
                      m.kind === 'request_for_assistance'
                        ? 'bg-amber-600/30 text-amber-200'
                        : 'bg-slate-700/50 text-slate-300'
                    }`}
                  >
                    {interopsKindLabel(m.kind)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap break-words text-[12px] text-slate-100">{m.body}</p>
                {/* Federal RFA caption (R1 / §6.7.2) — GOC as a caption, never a target. */}
                {m.kind === 'request_for_assistance' && hasFederalRecipient(m) && (
                  <p className="mt-1 text-[9px] italic text-slate-500" data-testid="rfa-federal-caption">
                    {RFA_FEDERAL_CAPTION}
                  </p>
                )}
                <div className="mt-1.5">
                  <RecipientBreakdown
                    recipients={m.recipients}
                    sentAt={m.created_at}
                    // Originator view (read-only OR reply): reading the recorded receipt states,
                    // never recording an out-of-app response (that is the PEOC's affordance) — so
                    // no callback is wired. The originator's two-way affordance is the reply
                    // composer below, not per-leg response-recording.
                    onRecordResponse={
                      isOriginatorView ? undefined : (recipient) => setRecording({ messageId: m.id, recipient })
                    }
                  />
                </div>
                {!isOriginatorView && myUnacked && (
                  <button
                    type="button"
                    data-testid="interops-acknowledge"
                    onClick={() => void handleAck(m.id, myUnacked.id)}
                    className="mt-1.5 rounded border border-teal-500/50 px-2 py-0.5 text-[11px] font-medium text-teal-300 hover:bg-teal-500/10"
                  >
                    {ACKNOWLEDGE_RECEIPT_LABEL}
                  </button>
                )}
              </li>
            );
          })
        )}
      </ul>

      {/* Full multi-destination composer — incident / standalone / the PEOC's assistance-request
          surface. Suppressed in the originator's read-only + reply views (reply uses the
          constrained composer below). */}
      {mode === 'full' && (
      <div className="flex flex-col gap-2" data-testid="interops-composer">
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Kind</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as InteropsMessageKind)}
            data-testid="interops-kind-select"
            className="rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[11px] text-slate-100"
          >
            <option value="information">{INFORMATION_KIND_LABEL}</option>
            <option value="request_for_assistance">{RFA_KIND_LABEL}</option>
          </select>
          <button
            type="button"
            data-testid="interops-fn-preset"
            onClick={applyFnPreset}
            className="ml-auto rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300 hover:bg-slate-800"
            title="Co-notify Indigenous Services Canada (PERP §6.7.1)"
          >
            {FN_PRESET_LABEL}
          </button>
        </div>

        <fieldset className="rounded border border-slate-800 p-2">
          <legend className="px-1 text-[10px] uppercase tracking-wide text-slate-500">
            {COMPOSER_RECIPIENTS_LABEL}
          </legend>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            <RecipientGroup
              title="Emergency Operations Centres & agencies"
              options={agencies.map((a) => ({ key: `a:${a.id}`, label: a.name }))}
              selected={selected}
              onToggle={toggle}
            />
            <RecipientGroup
              title="External partners"
              options={destinations.map((d) => ({ key: `d:${d.id}`, label: d.name }))}
              selected={selected}
              onToggle={toggle}
            />
          </div>
        </fieldset>

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder={COMPOSER_PLACEHOLDER}
          data-testid="interops-body"
          className="resize-none rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-500"
        />
        {error && <p className="text-[10px] text-rose-300">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!canSend}
            data-testid="interops-send"
            className="rounded bg-cyan-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
          >
            {sending ? SENDING_LABEL : kind === 'request_for_assistance' ? REQUEST_ASSISTANCE_LABEL : SEND_LABEL}
          </button>
        </div>
      </div>
      )}

      {/* #451 — the originator's CONSTRAINED reply composer: destination LOCKED to the PEOC
          counterparty (derived from the thread — no picker, never an external destination), kind
          Information, threaded to the message being answered. Renders only when a counterparty is
          derivable (needs the acting seat's identity → dormant under the AUTH-off single-seat demo,
          activating with the enforced seat identity, #190). Reuses SEND_LABEL + the A.2 "To …"
          convention (zero new operator strings). */}
      {mode === 'reply' && replyTarget && (
        <div className="flex flex-col gap-2" data-testid="interops-reply-composer">
          <p className="text-[10px] uppercase tracking-wide text-slate-500" data-testid="interops-reply-to">
            {outboundToLabel(replyTarget.recipientName || undefined)}
          </p>
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            rows={2}
            placeholder={COMPOSER_PLACEHOLDER}
            data-testid="interops-reply-body"
            className="resize-none rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-500"
          />
          {replyError && <p className="text-[10px] text-rose-300">{replyError}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleReply()}
              disabled={!canReply}
              data-testid="interops-reply-send"
              className="rounded bg-cyan-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {replySending ? SENDING_LABEL : SEND_LABEL}
            </button>
          </div>
        </div>
      )}

      {/* #567 — the "Record response" paste dialog (a record, not a send). The originator's views
          record nothing (the reply composer above is the two-way affordance), so the dialog can
          never open there. */}
      {mode === 'full' && recording && (
        <RecordResponseDialog
          destinationName={recording.recipient.recipient_destination_name ?? RECORDED_SOURCE_FALLBACK}
          saving={recordSaving}
          onCancel={() => setRecording(null)}
          onSubmit={(text) => void handleRecordResponse(text)}
        />
      )}
    </section>
  );
}

/** #567 — a recorded reply rendered as a recorded DOCUMENT: honest chrome naming the external
 *  party as the SOURCE + the operator as the recorder, the "recorded from outside the COP"
 *  sub-label, and the pasted body as quoted document text. Never implies the external party
 *  used the COP; never a read/ack claim on the original "Sent" leg.
 *
 *  RBAC Slice 3c — in the ORIGINATOR's read-only view the chrome is the party-neutral
 *  "Response recorded · HH:MM" (describes the WORK/leg, never a party — FN-dignity), keeping the
 *  same "recorded from outside the COP" sub-label + quoted body. */
export function RecordedResponseCard({
  message,
  readOnly = false,
}: {
  message: InteropsMessageView;
  readOnly?: boolean;
}): JSX.Element {
  return (
    <li
      data-testid="interops-recorded-response"
      className="rounded-lg border border-slate-700 border-dashed bg-slate-900/30 p-2"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] text-slate-300" data-testid="recorded-response-chrome">
          {readOnly
            ? responseRecordedAtLabel(hhmmUtc(message.created_at))
            : recordedResponseChromeLabel(
                message.recorded_source_destination_name ?? RECORDED_SOURCE_FALLBACK,
                hhmmUtc(message.created_at),
                message.author_agency_name ?? RECORDING_AGENCY_FALLBACK,
              )}
        </span>
        <span className="rounded bg-slate-700/50 px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-slate-300">
          {interopsKindLabel(message.kind)}
        </span>
      </div>
      <p className="mt-0.5 text-[9px] italic text-slate-500" data-testid="recorded-response-sublabel">
        {RECORDED_FROM_OUTSIDE_COP_SUBLABEL}
      </p>
      {/* The reply as a quoted document — plain, left-ruled, never re-styled as a COP message. */}
      <blockquote className="mt-1.5 border-l-2 border-slate-600 pl-2 text-[12px] whitespace-pre-wrap break-words text-slate-100">
        {message.body}
      </blockquote>
    </li>
  );
}

/** #567 — the paste dialog. Pure/controlled: the textarea + confirm/cancel; the parent owns the
 *  submit + reload. Explicit that this RECORDS a reply received outside the COP (not a send). */
export function RecordResponseDialog({
  destinationName,
  saving,
  onCancel,
  onSubmit,
}: {
  destinationName: string;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (body: string) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const canRecord = text.trim().length > 0 && !saving;
  return (
    <div
      data-testid="record-response-dialog"
      className="mt-2 rounded-lg border border-slate-600 bg-slate-900/70 p-2.5"
    >
      <h4 className="text-[11px] font-semibold text-slate-100">{RECORD_RESPONSE_DIALOG_TITLE}</h4>
      <p className="mt-0.5 text-[10px] leading-snug text-slate-400">
        {RECORD_RESPONSE_DIALOG_HINT} <span className="text-slate-300">({destinationName})</span>
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={RECORD_RESPONSE_PLACEHOLDER}
        data-testid="record-response-body"
        className="mt-1.5 w-full resize-none rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-500"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSubmit(text)}
          disabled={!canRecord}
          data-testid="record-response-confirm"
          className="rounded bg-cyan-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
        >
          {RECORD_RESPONSE_LABEL}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="record-response-cancel"
          className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
        >
          {RECORD_RESPONSE_CANCEL_LABEL}
        </button>
      </div>
    </div>
  );
}

function RecipientGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: Array<{ key: string; label: string }>;
  selected: Set<string>;
  onToggle: (key: string) => void;
}): JSX.Element {
  return (
    <div>
      <p className="mb-0.5 text-[10px] font-medium text-slate-400">{title}</p>
      <ul className="flex flex-col gap-0.5">
        {options.length === 0 ? (
          <li className="text-[10px] text-slate-600">None available.</li>
        ) : (
          options.map((o) => (
            <li key={o.key}>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-200">
                <input
                  type="checkbox"
                  checked={selected.has(o.key)}
                  onChange={() => onToggle(o.key)}
                  data-testid={`interops-recipient-${o.key}`}
                />
                <span>{o.label}</span>
              </label>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
